import React, { useEffect, useState } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Loader2, Download, TrendingUp } from "lucide-react";
import { API, money, btnGhost } from "./shared";

export const downloadCsv = async (params, headers) => {
  const res = await axios.get(`${API}/crm/transactions/export`, { headers, params, responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const name = /filename="([^"]+)"/.exec(cd)?.[1] || "transacciones.csv";
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("es-PA", { month: "short", year: "2-digit" });

const TransactionsReport = ({ headers }) => {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/crm/reports/transactions`, { headers, params: { month } })
      .then((r) => alive && setData(r.data))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = data?.totals;

  return (
    <div data-testid="tx-report" className="bg-white border border-black/[0.06] rounded-2xl p-4 sm:p-5 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-[#0fb013]" />
          <p className="text-[11px] font-bold uppercase tracking-wider text-black/45">Reporte de transacciones</p>
          {loading && <Loader2 size={13} className="animate-spin text-black/30" />}
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="report-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="text-[12px] font-bold text-black bg-white border-2 border-black/10 rounded-full px-3.5 py-1.5 focus:border-[#0fb013] focus:outline-none" />
          <button data-testid="report-export-csv" className={btnGhost} disabled={exporting}
            onClick={async () => { setExporting(true); try { await downloadCsv({ month }, headers); } finally { setExporting(false); } }}>
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} CSV del mes
          </button>
          <button data-testid="report-export-all" className={btnGhost} onClick={() => downloadCsv({}, headers)}>
            <Download size={13} /> CSV total
          </button>
        </div>
      </div>

      {t && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[["Ventas", t.ventas, "text-[#0fb013]"], ["Comisiones", t.comisiones, "text-black/70"], ["Cashouts", t.cashouts, "text-blue-600"], ["Reembolsos", t.reembolsos, "text-[#ff603e]"], ["Neto", t.neto, "text-black"]].map(([l, v, c]) => (
            <div key={l} className="bg-[#fafcf9] border border-black/[0.06] rounded-xl p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-black/40">{l}</p>
              <p data-testid={`report-${l.toLowerCase()}`} className={`mt-0.5 font-extrabold text-[17px] ${c}`}>{money(v)}</p>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-black/40 mb-2">Por día · {monthLabel(month)} ({t.count} aprobadas)</p>
            <div className="h-[220px]">
              {data.daily.length === 0 ? <p className="text-[12px] font-semibold text-black/40 py-16 text-center">Sin transacciones aprobadas este mes</p> : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.daily} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="rgba(0,0,0,0.05)" />
                    <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} tick={{ fontSize: 10, fontWeight: 700 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, fontSize: 12, fontWeight: 600 }} />
                    <Bar dataKey="ventas" name="Ventas" fill="#0fe814" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="comisiones" name="Comisiones" fill="#111111" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="cashouts" name="Cashouts" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-black/40 mb-2">Últimos 6 meses</p>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.months} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 10, fontWeight: 700 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, fontSize: 12, fontWeight: 600 }} />
                  <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} />
                  <Bar dataKey="ventas" name="Ventas" fill="#0fe814" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="comisiones" name="Comisiones" fill="#111111" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cashouts" name="Cashouts" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {data && data.top_clients?.length > 0 && (
        <div className="mt-5" data-testid="report-top-clients">
          <p className="text-[10px] font-bold uppercase tracking-wider text-black/40 mb-2">Ranking de comercios por ventas · {monthLabel(month)}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.top_clients.map((c, i) => {
              const pct = data.top_clients[0].ventas ? Math.max(4, (c.ventas / data.top_clients[0].ventas) * 100) : 0;
              return (
                <div key={c.app_id} data-testid={`report-top-${i + 1}`} className="bg-[#fafcf9] border border-black/[0.06] rounded-xl px-3.5 py-2.5">
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[12px] font-extrabold ${i === 0 ? "bg-[#0fe814] text-black" : "bg-black text-white"}`}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold text-black truncate">{c.name} <span className="text-[10px] font-bold text-[#0fb013]">{c.reference}</span></p>
                      <p className="text-[11px] font-medium text-black/45">{c.count} venta(s){c.category ? ` · ${c.category}` : ""}</p>
                    </div>
                    <span className="font-extrabold text-[14px] text-black whitespace-nowrap">{money(c.ventas)}</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-black/[0.06] overflow-hidden"><div className="h-full bg-[#0fe814] rounded-full" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsReport;
